import {
  beautyAddProfessionalApi,
  beautyAddSalonApi,
  beautyAddServiceApi,
  beautyGetSalonApi,
  beautyUpdateSalonApi,
} from "../../../api/organizerBeautyApi";
import {
  formDataAjustesImportados,
  formDataCamposImportados,
  formDataContacto,
  formDataCrearSalon,
  formDataFotos,
  hayFotos,
  payloadProfesional,
  payloadServicio,
} from "./salonPayload";

export const progresoInicial = () => ({ salon: null, servicios: {}, profesionales: {}, ajustesIntentados: false });

const idDe = (res) => {
  const info = res?.data?.info || {};
  return info._id || info.id;
};

// 409 = the negocio already has a salon (e.g. a previous attempt created it): continue with that one.
const crearSalon = async (d, opts) => {
  try {
    const res = await beautyAddSalonApi(formDataCrearSalon(d, opts));
    const id = idDe(res);
    if (!id) throw new Error("No pudimos confirmar el salón creado. Revisá Turnos Beauty.");
    return { id, salon: res.data.info };
  } catch (e) {
    if (e?.response?.status !== 409) throw e;
    const res = await beautyGetSalonApi();
    const salon = res?.data?.info;
    const id = salon?._id || salon?.id;
    if (!id) throw e;
    return { id, salon };
  }
};

// Settings the create request does not carry, for a copied salon. Best effort: the salon stays created
// and the notices say what was not copied.
const copiarAjustes = async (id, d) => {
  const avisos = [];
  try {
    await beautyUpdateSalonApi(formDataAjustesImportados(id, d));
  } catch (e) {
    avisos.push("No pudimos copiar los datos de contacto, los recordatorios ni el reagendado: configuralos en el salón.");
  }
  if (d.importado?.clientFields?.length) {
    try {
      await beautyUpdateSalonApi(formDataCamposImportados(id, d));
    } catch (e) {
      avisos.push("No pudimos copiar los campos de cliente: cargalos de nuevo en el salón.");
    }
  }
  return avisos;
};

/**
 * Publishes the salon: creates it, then its services in order, then its professionals. `progreso`
 * survives retries: whatever was created before a failure is never created twice.
 * Returns the context the optional steps use; `avisos` lists what a copy could not do.
 */
export const publicarSalon = async (d, progreso, { soloGratis = false, alCrearSalon } = {}) => {
  if (!progreso.salon) progreso.salon = await crearSalon(d, { soloGratis });
  // Multi-sede: los servicios y profesionales siguientes tienen que ir al salón recién creado, no al activo.
  alCrearSalon?.(progreso.salon.id);

  for (const s of d.servicios) {
    if (progreso.servicios[s.key]) continue;
    const id = idDe(await beautyAddServiceApi(payloadServicio(s)));
    if (!id) throw new Error(`No pudimos confirmar el servicio "${s.nombre.trim()}".`);
    progreso.servicios[s.key] = id;
  }

  for (const p of d.equipo) {
    if (progreso.profesionales[p.key]) continue;
    const id = idDe(await beautyAddProfessionalApi(payloadProfesional(p, d, (k) => progreso.servicios[k])));
    if (!id) throw new Error(`No pudimos confirmar a ${p.nombre.trim()}.`);
    progreso.profesionales[p.key] = id;
  }

  let avisos = [];
  if (d.importado && !progreso.ajustesIntentados) {
    progreso.ajustesIntentados = true;
    avisos = await copiarAjustes(progreso.salon.id, d);
  }

  return {
    ...progreso.salon,
    servicios: { ...progreso.servicios },
    profesionales: { ...progreso.profesionales },
    ...(avisos.length ? { avisos } : {}),
  };
};

// Saves one optional step through /salon/update; skipping saves nothing.
export const guardarOpcional = async (paso, d, ctx) => {
  const { id } = ctx;
  switch (paso.id) {
    case "contacto":
      await beautyUpdateSalonApi(formDataContacto(id, d));
      return;
    case "fotos":
      if (hayFotos(d)) await beautyUpdateSalonApi(formDataFotos(id, d));
      return;
    default:
  }
};
